import { Button, Drawer, Dropdown, Modal, Select, Switch } from 'antd';
import { DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import { useDrag } from 'react-dnd';
import { ActivityCategoryInfo } from '../../interface';
import { clearScheduleConfirmCopy, type ScheduleTemplate } from './scheduleUtils';

const DraggableEvent: React.FC<{
    event: ScheduleTemplate;
    onDragStart: () => void;
    onDelete: (eventId: number) => void;
    onEdit: (event: ScheduleTemplate) => void;
}> = ({ event, onDragStart, onDelete, onEdit }) => {
    const [{ isDragging }, drag] = useDrag(() => ({
        type: 'template',
        item: { ...event },
        canDrag: !event.used,
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging(),
        }),
    }), [event.used]);

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        Modal.confirm({
            title: 'Reset Match',
            content: `Are you sure you want to reset ${event.title}?`,
            okText: 'Confirm',
            cancelText: 'Cancel',
            onOk: () => onDelete(event.id),
        });
    };

    return (
        <div
            ref={drag}
            className={`event-template ${event.used ? 'disabled' : ''}`}
            style={{
                opacity: isDragging ? 0.5 : event.used ? 0.5 : 1,
                cursor: event.used ? 'not-allowed' : 'move',
            }}
            onMouseDown={onDragStart}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <span>
                    {event.title} ({event.duration} Minutes)
                    {event.used && <span className="used-badge">Added</span>}
                </span>
                <div className="event-actions">
                    {event.used ? (
                        <button
                            className="delete-btn"
                            onClick={handleDelete}
                            style={{ marginLeft: '8px' }}
                        >×</button>
                    ) : (
                        <SettingOutlined
                            className="edit-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit(event);
                            }}
                            style={{ marginLeft: '8px', cursor: 'pointer' }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

const emptyTemplateMessage = (templates: ScheduleTemplate[]) =>
    templates.length > 0
        ? 'All matches are scheduled. Turn on Show added to review them.'
        : 'No schedule blocks are available in this category.';

const ClearScheduleControl: React.FC<{
    currentDate: string;
    allCategories: boolean;
    onResetSchedule: (currentDateOnly: boolean) => void;
}> = ({ currentDate, allCategories, onResetSchedule }) => {
    const confirmClear = (currentDateOnly: boolean) => {
        const copy = clearScheduleConfirmCopy(currentDateOnly, currentDate, allCategories);
        Modal.confirm({
            title: copy.title,
            content: copy.content,
            okText: 'Clear',
            cancelText: 'Cancel',
            onOk: () => onResetSchedule(currentDateOnly),
        });
    };

    return (
        <div style={{ marginTop: '10px', textAlign: 'right' }}>
            <Dropdown
                menu={{
                    items: [
                        {
                            key: 'date',
                            label: 'Clear this date',
                            onClick: () => confirmClear(true),
                        },
                        {
                            key: 'all',
                            label: 'Clear all dates',
                            onClick: () => confirmClear(false),
                        },
                    ],
                }}
                trigger={['click']}
            >
                <Button type="text" danger icon={<DeleteOutlined />} aria-label="Clear scheduled matches">
                    Clear
                </Button>
            </Dropdown>
        </div>
    );
};

interface ScheduleTemplateListProps {
    categories: ActivityCategoryInfo[] | null;
    nowCategory: number;
    onCategoryChange: (categoryId: number) => void;
    templates: ScheduleTemplate[];
    visibleTemplates: ScheduleTemplate[];
    showAdded: boolean;
    onShowAddedChange: (showAdded: boolean) => void;
    selectedCategoryHasMatches: boolean;
    onDragStart: (template: ScheduleTemplate) => void;
    onReset: (id: number) => void;
    onResetSchedule: (currentDateOnly: boolean) => void;
    currentDate: string;
    onEdit: (template: ScheduleTemplate) => void;
}

export const ScheduleTemplateSidebar: React.FC<ScheduleTemplateListProps> = ({
    categories,
    nowCategory,
    onCategoryChange,
    templates,
    visibleTemplates,
    showAdded,
    onShowAddedChange,
    selectedCategoryHasMatches,
    onDragStart,
    onReset,
    onResetSchedule,
    currentDate,
    onEdit,
}) => (
    <div className="template-list">
        <div className="template-list-header">
            <div className="template-list-summary">
                <strong>Matches</strong>
                <label className="template-list-toggle template-list-summary-toggle">
                    <Switch
                        size="small"
                        checked={showAdded}
                        onChange={onShowAddedChange}
                    />
                    <span>Show added</span>
                </label>
            </div>
            <Select style={{ width: '100%', marginBottom: '10px' }} value={nowCategory} onChange={onCategoryChange}>
                <Select.Option value={0} key="0">ALL</Select.Option>
                {categories?.map((category) => (
                    <Select.Option key={category.id} value={category.id}>
                        {category.name}
                    </Select.Option>
                ))}
            </Select>
            {!selectedCategoryHasMatches && (
                <div style={{
                    padding: '10px',
                    backgroundColor: '#fffbe6',
                    border: '1px solid #ffe58f',
                    borderRadius: '4px',
                    margin: '10px 0',
                    color: '#666',
                }}>
                    This category has no schedule blocks yet. Complete Category Setup to generate them.
                </div>
            )}
        </div>

        <div className="template-list-content">
            {visibleTemplates.length > 0 ? (
                visibleTemplates.map((template) => (
                    <DraggableEvent
                        key={template.id}
                        event={template}
                        onDragStart={() => onDragStart(template)}
                        onDelete={onReset}
                        onEdit={onEdit}
                    />
                ))
            ) : (
                <div className="template-list-empty">
                    {emptyTemplateMessage(templates)}
                </div>
            )}

            <ClearScheduleControl
                currentDate={currentDate}
                allCategories={nowCategory === 0}
                onResetSchedule={onResetSchedule}
            />
        </div>
    </div>
);

export const ScheduleTemplateDrawer: React.FC<ScheduleTemplateListProps & {
    visible: boolean;
    onClose: () => void;
}> = ({
    visible,
    onClose,
    categories,
    nowCategory,
    onCategoryChange,
    templates,
    visibleTemplates,
    showAdded,
    onShowAddedChange,
    onReset,
    onResetSchedule,
    currentDate,
    onEdit,
}) => (
    <Drawer
        title="Add Match"
        placement="bottom"
        height="60%"
        onClose={onClose}
        open={visible}
    >
        <div style={{ marginBottom: '16px' }}>
            <Select style={{ width: '100%' }} value={nowCategory} onChange={onCategoryChange}>
                <Select.Option value={0} key="0">ALL</Select.Option>
                {categories?.map((category) => (
                    <Select.Option key={category.id} value={category.id}>
                        {category.name}
                    </Select.Option>
                ))}
            </Select>
            <label className="template-list-toggle mobile-template-toggle">
                <Switch
                    size="small"
                    checked={showAdded}
                    onChange={onShowAddedChange}
                />
                <span>Show added</span>
            </label>
        </div>
        <div className="mobile-template-list">
            {visibleTemplates.length > 0 ? (
                visibleTemplates.map((template) => (
                    <button
                        type="button"
                        key={template.id}
                        className={`event-template mobile-template-item ${template.used ? 'disabled' : ''}`}
                        onClick={() => {
                            if (!template.used) {
                                onEdit(template);
                                return;
                            }
                            Modal.confirm({
                                title: 'Reset Match',
                                content: `Remove ${template.title} from the schedule?`,
                                okText: 'Remove',
                                cancelText: 'Cancel',
                                onOk: () => onReset(template.id),
                            });
                        }}
                    >
                        {template.title} ({template.duration} Minutes)
                        {template.used && <span className="used-badge">Added</span>}
                    </button>
                ))
            ) : (
                <div className="template-list-empty">
                    {emptyTemplateMessage(templates)}
                </div>
            )}
            <ClearScheduleControl
                currentDate={currentDate}
                allCategories={nowCategory === 0}
                onResetSchedule={onResetSchedule}
            />
        </div>
    </Drawer>
);
